import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PromptWithVersions } from "@/types/prompt";
import { supabase } from "../../../supabase/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Edit, Eye, History, Trash2, Search, FileText, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganization } from "../../App";
import { useAuth } from "../../../supabase/auth";
import { NoData } from "@/components/ui/no-data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function PromptsList() {
  const [prompts, setPrompts] = useState<PromptWithVersions[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  useEffect(() => {
    if (currentOrganization) {
      fetchPrompts();
    } else {
      // Clear prompts if no organization is selected
      setPrompts([]);
      setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (!currentOrganization) return;

    // Set up realtime subscription with organization filter
    const subscription = supabase
      .channel(`prompts-changes-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "prompts",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => fetchPrompts(),
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentOrganization]);

  const fetchPrompts = async () => {
    if (!currentOrganization) {
      setPrompts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data: promptsData, error: promptsError } = await supabase
        .from("prompts")
        .select("*")
        .eq("organization_id", currentOrganization.id)
        .order("created_at", { ascending: false });

      if (promptsError) {
        console.error("Error fetching prompts:", promptsError);
        toast({
          title: "Error",
          description: "Failed to load prompts. Please try again.",
          variant: "destructive",
        });
        setPrompts([]);
        return;
      }

      // Fetch the latest version for each prompt
      const promptsWithVersions = await Promise.all(
        promptsData.map(async (prompt) => {
          const { data: versionsData, error: versionsError } = await supabase
            .from("prompt_versions")
            .select("*")
            .eq("prompt_id", prompt.id)
            .order("version_number", { ascending: false })
            .limit(1);

          if (versionsError) {
            console.error("Error fetching prompt versions:", versionsError);
            return {
              ...prompt,
              versions: [],
              latest_version: undefined,
            };
          }

          return {
            ...prompt,
            versions: versionsData || [],
            latest_version:
              versionsData && versionsData.length > 0
                ? versionsData[0]
                : undefined,
          };
        }),
      );

      setPrompts(promptsWithVersions);
    } catch (error) {
      console.error("Error fetching prompts:", error);
      toast({
        title: "Error",
        description: "Failed to load prompts. Please try again.",
        variant: "destructive",
      });
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPrompt = (promptId: string) => {
    navigate(`/prompt-engine/prompt/${promptId}`);
  };

  const handleEditPrompt = (promptId: string) => {
    navigate(`/prompt-engine/edit/${promptId}`);
  };

  const handleViewHistory = (promptId: string) => {
    navigate(`/prompt-engine/history/${promptId}`);
  };

  const handleDeletePrompt = async (promptId: string) => {
    try {
      // Delete all versions first (due to foreign key constraints)
      const { error: versionsError } = await supabase
        .from("prompt_versions")
        .delete()
        .eq("prompt_id", promptId);

      if (versionsError) throw versionsError;

      // Then delete the prompt
      const { error: promptError } = await supabase
        .from("prompts")
        .delete()
        .eq("id", promptId);

      if (promptError) throw promptError;

      // Update the local state
      setPrompts(prompts.filter((prompt) => prompt.id !== promptId));

      toast({
        title: "Success",
        description: "Prompt deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting prompt:", error);
      toast({
        title: "Error",
        description: "Failed to delete prompt. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredPrompts = prompts.filter(
    (prompt) =>
      prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (prompt.description &&
        prompt.description.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  return (
    <div className="space-y-6">
      {/* Only show search when loading or when there's data */}
      {(loading || prompts.length > 0) && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search prompts..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : prompts.length === 0 ? (
        <NoData
          icon={<FileText className="h-12 w-12 text-muted-foreground/50" />}
          title="No prompts found"
          description="You haven't created any prompts yet. Create your first prompt to get started."
          actionButton={
            <Button
              variant="primaryOutline"
              onClick={() => navigate("/prompt-engine/create")}
              className="mt-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Prompt
            </Button>
          }
        />
      ) : filteredPrompts.length === 0 ? (
        <NoData
          icon={<FileText className="h-12 w-12 text-muted-foreground/50" />}
          title="No prompts match your search"
          description="No prompts match your search criteria. Try a different search term."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPrompts.map((prompt) => (
            <Card key={prompt.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{prompt.title}</CardTitle>
                <CardDescription>
                  {new Date(prompt.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {prompt.description || "No description"}
                </p>
                {prompt.latest_version && (
                  <div className="mt-2 flex items-center text-xs text-muted-foreground">
                    <History className="h-3 w-3 mr-1" />
                    <span>Version {prompt.latest_version.version_number}</span>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewPrompt(prompt.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewHistory(prompt.id)}
                  >
                    <History className="h-4 w-4 mr-1" /> History
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDeletePrompt(prompt.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
